import { useEffect, useRef, useState } from "react";
import { useAdminUrlState } from "./admin-navigation";
import { AdminCustomerLinks } from "./AdminCustomerLinks";
import { apiRequest } from "../api";
import { Dialog, EmptyState, Field, StatusPill } from "../components";

export type AdminDirectoryKind = "users" | "teams";

interface AdminUserListItem {
  id: string;
  username: string | null;
  role: string | null;
  banned: boolean;
  createdAt: string;
  bomana: {
    status: string;
    expiresAt: string | null;
    version: number;
  } | null;
  devices: {
    active: number;
    total: number;
    max: number;
  };
}

interface AdminUserDetail extends AdminUserListItem {
  entitlements: Array<{
    id: string;
    appId: string;
    productCode: string;
    productName: string;
    status: string;
    expiresAt: string | null;
    version: number;
    features: string[];
  }>;
  recentGrants: Array<{
    id: string;
    type: string;
    createdAt: string;
    resultingExpiry: string | null;
  }>;
  recentAudit: Array<{
    action: string;
    createdAt: string;
    reason: string | null;
  }>;
}

interface AdminTeamListItem {
  id: string;
  name: string;
  status: string;
  authorizationVersion: number;
  owner: { id: string; username: string | null };
  memberCount: number;
  maxMembers: number;
  adminCount: number;
  divisionCount: number;
  inviteCount: number;
  entitlement: {
    planCode: string;
    status: string;
    expiresAt: string | null;
    version: number;
  } | null;
  createdAt: string;
}

interface AdminTeamDetail extends AdminTeamListItem {
  admins: Array<{ id: string; username: string | null }>;
  members: Array<{ id: string; username: string | null }>;
  divisions: Array<{ id: string; name: string }>;
  pendingInvites: Array<{ id: string; createdAt: string; expiresAt: string | null }>;
  recentAudit: Array<{
    action: string;
    createdAt: string;
    reason: string | null;
  }>;
}

interface AdminDirectoryWorkspaceProps {
  kind: AdminDirectoryKind;
}

const PAGE_SIZE = "25";
export const TEAM_TRIAL_PLANS = ["team-100", "team-500", "team-1000"] as const;
export const DEFAULT_TEAM_TRIAL_DURATION_DAYS = 30;

export function AdminDirectoryWorkspace({ kind }: AdminDirectoryWorkspaceProps) {
  const [query, setQuery] = useAdminUrlState(`${kind}.query`, "");
  const [queryInput, setQueryInput] = useState(query);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [teams, setTeams] = useState<AdminTeamListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useAdminUrlState<string | null>(`${kind}.selected`, null);
  useEffect(() => { setQueryInput(query); }, [query]);
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [teamDetail, setTeamDetail] = useState<AdminTeamDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [detailReloadVersion, setDetailReloadVersion] = useState(0);
  const [trialTeam, setTrialTeam] = useState<AdminTeamDetail | null>(null);
  const listRequestVersion = useRef(0);
  const detailRequestVersion = useRef(0);
  const loadingMoreCursor = useRef<string | null>(null);

  const items = kind === "users" ? users : teams;
  const title = kind === "users" ? "用户目录" : "战队目录";
  const description =
    kind === "users"
      ? "只读查看账户、Bomana 权益与设备摘要；不显示敏感认证信息。"
      : "查看受控商业战队、成员规模、权益与审计摘要；可增加体验授权，不包含房间、席位租约或实时战术状态。";

  const loadList = async (cursor?: string, append = false) => {
    if (append && (!cursor || loadingMoreCursor.current === cursor)) {
      return;
    }
    const requestVersion = ++listRequestVersion.current;
    if (append) {
      loadingMoreCursor.current = cursor ?? null;
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const search = new URLSearchParams({ query, limit: PAGE_SIZE });
      if (cursor) {
        search.set("cursor", cursor);
      }
      if (kind === "users") {
        const result = await apiRequest<{
          users: AdminUserListItem[];
          nextCursor: string | null;
        }>(`/api/admin/users?${search.toString()}`);
        if (requestVersion !== listRequestVersion.current) {
          return;
        }
        setUsers((current) => append ? appendUnique(current, result.users) : result.users);
        setNextCursor(result.nextCursor);
        if (!append) {
          setSelectedId((current) =>
            result.users.some((user) => user.id === current)
              ? current
              : (result.users[0]?.id ?? null),
          );
        }
      } else {
        const result = await apiRequest<{
          teams: AdminTeamListItem[];
          nextCursor: string | null;
        }>(`/api/admin/teams?${search.toString()}`);
        if (requestVersion !== listRequestVersion.current) {
          return;
        }
        setTeams((current) => append ? appendUnique(current, result.teams) : result.teams);
        setNextCursor(result.nextCursor);
        if (!append) {
          setSelectedId((current) =>
            result.teams.some((team) => team.id === current)
              ? current
              : (result.teams[0]?.id ?? null),
          );
        }
      }
    } catch (caught) {
      if (requestVersion === listRequestVersion.current) {
        setError(caught instanceof Error ? caught.message : `无法读取${title}`);
      }
    } finally {
      if (requestVersion === listRequestVersion.current) {
        if (append) {
          setLoadingMore(false);
          loadingMoreCursor.current = null;
        } else {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    listRequestVersion.current += 1;
    detailRequestVersion.current += 1;
    loadingMoreCursor.current = null;
    setNextCursor(null);
    setUserDetail(null);
    setTeamDetail(null);
    setError("");
    void loadList();
  }, [kind, query]);

  useEffect(() => {
    const requestVersion = ++detailRequestVersion.current;
    if (!selectedId) {
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setError("");
    const endpoint = kind === "users" ? "/api/admin/users" : "/api/admin/teams";
    void apiRequest<{ user: AdminUserDetail } | { team: AdminTeamDetail }>(
      `${endpoint}/${encodeURIComponent(selectedId)}`,
    )
      .then((result) => {
        if (requestVersion !== detailRequestVersion.current) {
          return;
        }
        if ("user" in result) {
          setUserDetail(result.user);
        } else {
          setTeamDetail(result.team);
        }
      })
      .catch((caught) => {
        if (requestVersion === detailRequestVersion.current) {
          setError(caught instanceof Error ? caught.message : `无法读取${title}详情`);
        }
      })
      .finally(() => {
        if (requestVersion === detailRequestVersion.current) {
          setDetailLoading(false);
        }
      });
  }, [detailReloadVersion, kind, selectedId]);

  const search = () => {
    setQuery(queryInput.trim());
  };

  return (
    <article className="dashboard-card admin-directory-workspace">
      <div className="card-title-row admin-directory-heading">
        <div>
          <span className="card-kicker">{kind === "users" ? "只读目录" : "商业授权"}</span>
          <h2>{title}</h2>
          <p className="admin-card-note">{description}</p>
        </div>
        <StatusPill tone="info">{kind === "users" ? "只读" : "受控商业授权"}</StatusPill>
      </div>

      <form
        className="admin-directory-search"
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <label className="field">
          <span className="field-label">搜索{kind === "users" ? "用户名或账户 ID" : "战队名称或 ID"}</span>
          <input
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder={kind === "users" ? "输入用户名或账户 ID" : "输入战队名称或 ID"}
            value={queryInput}
          />
        </label>
        <button className="secondary-button" disabled={loading} type="submit">
          搜索
        </button>
      </form>

      {error ? (
        <div className="form-error admin-directory-error" role="alert">
          {error} 已保留最近一次成功读取的内容。
          <button className="text-button" onClick={() => void loadList()} type="button">
            重试
          </button>
        </div>
      ) : null}
      {notice ? <div className="form-message admin-directory-notice">{notice}</div> : null}

      <div className="admin-directory-layout">
        <div className="admin-directory-list" aria-busy={loading || undefined}>
          {loading && !items.length ? <p className="page-loading">正在读取{title}…</p> : null}
          {kind === "users"
            ? users.map((user) => (
                <button
                  aria-current={selectedId === user.id ? "page" : undefined}
                  className={`admin-directory-item ${selectedId === user.id ? "active" : ""}`}
                  key={user.id}
                  onClick={() => setSelectedId(user.id)}
                  type="button"
                >
                  <strong>{user.username ?? "未设置用户名"}</strong>
                  <small>{truncateId(user.id)} · {user.banned ? "已禁用" : "正常"}</small>
                  <small>{user.bomana ? `Bomana ${user.bomana.status}` : "无 Bomana 权益"}</small>
                </button>
              ))
            : teams.map((team) => (
                <button
                  aria-current={selectedId === team.id ? "page" : undefined}
                  className={`admin-directory-item ${selectedId === team.id ? "active" : ""}`}
                  key={team.id}
                  onClick={() => setSelectedId(team.id)}
                  type="button"
                >
                  <strong>{team.name}</strong>
                  <small>{truncateId(team.id)} · {team.status}</small>
                  <small>{team.memberCount}/{team.maxMembers} 成员 · v{team.authorizationVersion}</small>
                </button>
              ))}
          {!loading && !items.length ? (
            <EmptyState title={`没有匹配的${kind === "users" ? "用户" : "战队"}`}>
              调整搜索条件后重试。
            </EmptyState>
          ) : null}
          {nextCursor ? (
            <button
              className="secondary-button admin-directory-more"
              disabled={loading || loadingMore}
              onClick={() => void loadList(nextCursor, true)}
              type="button"
            >
              {loadingMore ? "正在加载…" : "加载更多"}
            </button>
          ) : null}
        </div>

        <div className="admin-directory-detail">
          {detailLoading ? <p className="page-loading">正在读取详情…</p> : null}
          {!detailLoading && kind === "users" && userDetail ? <UserDetail user={userDetail} /> : null}
          {!detailLoading && kind === "teams" && teamDetail ? <TeamDetail team={teamDetail} onTrialGrant={() => setTrialTeam(teamDetail)} /> : null}
          {!detailLoading && !selectedId ? (
            <p className="muted">从左侧选择一项查看{kind === "users" ? "只读摘要" : "商业授权摘要"}。</p>
          ) : null}
        </div>
      </div>
      {trialTeam ? (
        <TeamTrialGrantDialog
          onClose={() => setTrialTeam(null)}
          onGranted={(planCode, durationDays) => {
            setNotice(`已为“${trialTeam.name}”增加 ${planCode} 体验授权 ${durationDays} 天；详情正在重新读取。`);
            setDetailReloadVersion((current) => current + 1);
          }}
          team={trialTeam}
        />
      ) : null}
    </article>
  );
}

function UserDetail({ user }: { user: AdminUserDetail }) {
  return (
    <div className="admin-directory-detail-content">
      <div className="admin-directory-detail-heading">
        <div>
          <span className="card-kicker">账户</span>
          <h3>{user.username ?? "未设置用户名"}</h3>
          <AdminCustomerLinks userId={user.id} username={user.username} />
          <small>{truncateId(user.id)} · 注册于 {formatDate(user.createdAt)}</small>
        </div>
        <StatusPill tone={user.banned ? "danger" : "good"}>{user.banned ? "已禁用" : "正常"}</StatusPill>
      </div>
      <dl className="admin-directory-facts">
        <Fact label="角色" value={user.role ?? "用户"} />
        <Fact label="Bomana 权益" value={user.bomana ? `${user.bomana.status} · ${formatExpiry(user.bomana.expiresAt)} · v${user.bomana.version}` : "无"} />
        <Fact label="设备" value={`${user.devices.active}/${user.devices.total}/${user.devices.max}（活跃/总数/上限）`} />
      </dl>
      <DetailGroup title="权益">
        {user.entitlements.map((entitlement) => (
          <p key={entitlement.id}>{entitlement.productName}（{entitlement.productCode}）· {entitlement.status} · {formatExpiry(entitlement.expiresAt)} · v{entitlement.version}</p>
        ))}
      </DetailGroup>
      <DetailGroup title="最近授权">
        {user.recentGrants.map((grant) => <p key={grant.id}>{grant.type} · {formatDate(grant.createdAt)} · 到期 {formatExpiry(grant.resultingExpiry)}</p>)}
      </DetailGroup>
      <AuditSummary entries={user.recentAudit} />
    </div>
  );
}

function TeamDetail({ team, onTrialGrant }: { team: AdminTeamDetail; onTrialGrant: () => void }) {
  return (
    <div className="admin-directory-detail-content">
      <div className="admin-directory-detail-heading">
        <div>
          <span className="card-kicker">商业战队</span>
          <h3>{team.name}</h3>
          <small>{truncateId(team.id)} · 创建于 {formatDate(team.createdAt)}</small>
        </div>
        <StatusPill tone={team.status === "ACTIVE" ? "good" : "neutral"}>{team.status}</StatusPill>
      </div>
      <dl className="admin-directory-facts">
        <Fact label="所有者" value={team.owner.username ?? truncateId(team.owner.id)} />
        <Fact label="成员" value={`${team.memberCount}/${team.maxMembers} · 管理员 ${team.adminCount} · 分队 ${team.divisionCount} · 待邀请 ${team.inviteCount}`} />
        <Fact label="战队权益" value={team.entitlement ? `${team.entitlement.planCode} · ${team.entitlement.status} · ${formatExpiry(team.entitlement.expiresAt)} · v${team.entitlement.version}` : "无"} />
        <Fact label="授权版本" value={`v${team.authorizationVersion}`} />
      </dl>
      <div className="admin-team-trial-action">
        <button className="secondary-button" onClick={onTrialGrant} type="button">
          增加体验授权
        </button>
        <small>仅写入商业控制面权益；不涉及房间、席位或实时战术状态。</small>
      </div>
      <DetailGroup title="管理员">{team.admins.map((admin) => <p key={admin.id}>{admin.username ?? truncateId(admin.id)}</p>)}</DetailGroup>
      <DetailGroup title="成员">{team.members.map((member) => <p key={member.id}>{member.username ?? truncateId(member.id)}</p>)}</DetailGroup>
      <DetailGroup title="分队">{team.divisions.map((division) => <p key={division.id}>{division.name}</p>)}</DetailGroup>
      <DetailGroup title="待处理邀请">{team.pendingInvites.map((invite) => <p key={invite.id}>创建于 {formatDate(invite.createdAt)} · 到期 {formatExpiry(invite.expiresAt)}</p>)}</DetailGroup>
      <AuditSummary entries={team.recentAudit} />
    </div>
  );
}

export function TeamTrialGrantDialog({
  onClose,
  onGranted,
  team,
}: {
  onClose: () => void;
  onGranted: (planCode: (typeof TEAM_TRIAL_PLANS)[number], durationDays: number) => void;
  team: Pick<AdminTeamDetail, "id" | "name">;
}) {
  const [planCode, setPlanCode] = useState<(typeof TEAM_TRIAL_PLANS)[number]>("team-100");
  const [durationDays, setDurationDays] = useState(String(DEFAULT_TEAM_TRIAL_DURATION_DAYS));
  const [reason, setReason] = useState("");
  const [manualRef, setManualRef] = useState(() => createTeamTrialManualRef());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedDays = Number(durationDays);
    const normalizedReason = reason.trim();
    const normalizedManualRef = manualRef.trim();
    if (
      !Number.isInteger(normalizedDays) ||
      normalizedDays < 1 ||
      normalizedDays > 365 ||
      !normalizedReason ||
      !normalizedManualRef
    ) {
      setError("请选择计划，填写 1-365 天、后台原因和人工凭据。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/api/admin/teams/${encodeURIComponent(team.id)}/trial-grants`, {
        method: "POST",
        body: JSON.stringify({
          planCode,
          durationDays: normalizedDays,
          reason: normalizedReason,
          manualRef: normalizedManualRef,
        }),
      });
      onGranted(planCode, normalizedDays);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法增加战队体验授权");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      description={`将为商业战队“${team.name}”写入体验权益。此操作不创建房间、不分配席位，也不影响实时战术状态。`}
      dismissible={!busy}
      footer={
        <>
          <button className="button" disabled={busy} form="team-trial-grant-form" type="submit">
            {busy ? "正在写入…" : "确认增加体验授权"}
          </button>
          <button className="secondary-button" disabled={busy} onClick={onClose} type="button">
            取消
          </button>
        </>
      }
      onClose={onClose}
      open
      title="增加战队体验授权"
    >
      <form className="admin-team-trial-form" id="team-trial-grant-form" onSubmit={submit}>
        <Field label="体验计划">
          <select disabled={busy} onChange={(event) => setPlanCode(event.target.value as (typeof TEAM_TRIAL_PLANS)[number])} value={planCode}>
            {TEAM_TRIAL_PLANS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
          </select>
        </Field>
        <Field label="体验天数" hint="默认 30 天；可填写 1-365 天。">
          <input disabled={busy} inputMode="numeric" max="365" min="1" onChange={(event) => setDurationDays(event.target.value)} required type="number" value={durationDays} />
        </Field>
        <Field label="后台原因" hint="必填，将写入商业控制面审计。">
          <textarea disabled={busy} maxLength={500} onChange={(event) => setReason(event.target.value)} required rows={3} value={reason} />
        </Field>
        <Field label="人工凭据" hint="自动生成；保留不变可安全重试并避免重复授权。">
          <input autoComplete="off" disabled={busy} onChange={(event) => setManualRef(event.target.value)} required value={manualRef} />
        </Field>
        {error ? <div className="form-error" role="alert">{error}</div> : null}
      </form>
    </Dialog>
  );
}

function DetailGroup({ children, title }: { children: React.ReactNode; title: string }) {
  return <section className="admin-directory-detail-group"><h4>{title}</h4>{children}</section>;
}

function AuditSummary({ entries }: { entries: Array<{ action: string; createdAt: string; reason: string | null }> }) {
  return <DetailGroup title="最近审计">{entries.map((entry, index) => <p key={`${entry.action}-${entry.createdAt}-${index}`}>{entry.action} · {formatDate(entry.createdAt)}{entry.reason ? ` · ${entry.reason}` : ""}</p>)}</DetailGroup>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <><dt>{label}</dt><dd>{value}</dd></>;
}

function appendUnique<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  const knownIds = new Set(current.map((item) => item.id));
  return [...current, ...incoming.filter((item) => !knownIds.has(item.id))];
}

export function truncateId(value: string): string {
  return value.length <= 14 ? value : `${value.slice(0, 8)}…${value.slice(-4)}`;
}

export function createTeamTrialManualRef(now = Date.now()): string {
  return `team-trial-${now.toString(36)}`;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { dateStyle: "short", timeStyle: "short" });
}

function formatExpiry(value: string | null): string {
  return value ? formatDate(value) : "未设置";
}
